<!-- ----------------------------------------- template ----------------------------------------- -->
<template>
  <div class="support-tickets-page">
    <div class="instruction-box">
      <h3>Support Tickets</h3>
      <p>
        Submit support tickets to get help with any issues or questions you have about eClub.
        Our support team will respond to your tickets as soon as possible.
      </p>
    </div>

    <div class="tickets-container">
      <div class="tickets-list" :class="{ 'with-detail': selectedTicketId }">
        <div style="display: flex; justify-content: flex-end; margin-bottom: 16px;">
          <el-button type="primary" @click="openNewTicketDialog">
            New Ticket
          </el-button>
        </div>

        <div v-loading="isLoading">
          <template v-if="tickets && tickets.length > 0">
            <div
              v-for="ticket in tickets"
              :key="ticket._id.$id"
              class="ticket-item"
              :class="{ 'selected': ticket._id.$id === selectedTicketId }"
              @click="handleTicketClick(ticket)"
            >
              <div class="ticket-header">
                <h3>{{ ticket.subject || 'Support Ticket' }}</h3>
                <el-tag :type="getStatusType(ticket.status)" size="small">
                  {{ ticket.status || 'open' }}
                </el-tag>
              </div>
              <p class="ticket-content-preview">{{ ticket.message_content }}</p>
              <div class="ticket-meta">
                <span>{{ formatDate(ticket.create_time) }}</span>
                <span v-if="ticket.reply_info">
                  <i class="el-icon-chat-dot-round"></i> Replied
                </span>
              </div>
            </div>
          </template>
          <empty-state v-else message="No support tickets" description="Submit a new ticket to get help from our support team."></empty-state>
        </div>
      </div>

      <div v-if="selectedTicketId" class="ticket-detail">
        <el-button class="close-button" type="text" @click="selectedTicketId = null">
          <i class="el-icon-close"></i>
        </el-button>

        <div class="detail-content">
          <div class="detail-header">
            <h2>{{ selectedTicket?.subject || 'Support Ticket' }}</h2>
            <el-tag :type="getStatusType(selectedTicket?.status)" size="medium">
              {{ selectedTicket?.status || 'open' }}
            </el-tag>
          </div>
          <div class="ticket-meta">
            <span>{{ formatDate(selectedTicket?.create_time) }}</span>
          </div>
          <div class="ticket-message">
            <h4>Your Message:</h4>
            <p>{{ selectedTicket?.message_content }}</p>
          </div>
        </div>

        <div class="reply-section" v-if="selectedTicket?.reply_info">
          <h3>Admin Reply</h3>
          <div class="reply-item">
            <div class="reply-meta">
              <span class="reply-author">Support Team</span>
              <span class="reply-time">{{ formatDate(selectedTicket?.reply_info?.create_time) }}</span>
            </div>
            <div class="reply-content">
              {{ selectedTicket?.reply_info?.message_content }}
            </div>
          </div>
        </div>

        <div v-else class="waiting-reply">
          <i class="el-icon-time"></i>
          <span>Waiting for support team reply...</span>
        </div>
      </div>
    </div>

    <!-- New Ticket Dialog -->
    <el-dialog
      title="New Support Ticket"
      :visible.sync="dialogVisible"
      width="50%"
      @close="handleDialogClose"
    >
      <el-form :model="newTicket" :rules="rules" ref="ticketForm" label-width="100px">
        <el-form-item label="Subject" prop="subject">
          <el-input v-model="newTicket.subject" placeholder="Enter ticket subject"></el-input>
        </el-form-item>

        <el-form-item label="Message" prop="content">
          <el-input
            type="textarea"
            v-model="newTicket.content"
            :rows="6"
            placeholder="Describe your issue or question in detail..."
          ></el-input>
        </el-form-item>
      </el-form>

      <span slot="footer" class="dialog-footer">
        <el-button @click="dialogVisible = false">Cancel</el-button>
        <el-button type="primary" @click="submitTicket" :loading="submitting">Submit</el-button>
      </span>
    </el-dialog>
  </div>
</template>

<!-- ------------------------------------------ script ------------------------------------------ -->
<script>
import { postRequest } from '@/api/eclub'
import { getUserId, getUserType } from '@/utils/store'
import EmptyState from '@/components/EmptyState.vue'

export default {
  name: 'SupportTickets',
  components: {
    EmptyState
  },
  data() {
    return {
      tickets: [],
      isLoading: false,
      submitting: false,
      dialogVisible: false,
      selectedTicketId: null,
      selectedTicket: null,
      newTicket: {
        subject: '',
        content: ''
      },
      rules: {
        subject: [
          { required: true, message: 'Please enter a subject', trigger: 'blur' },
          { min: 3, max: 100, message: 'Length should be 3 to 100 characters', trigger: 'blur' }
        ],
        content: [
          { required: true, message: 'Please enter your message', trigger: 'blur' },
          { min: 10, message: 'Message should be at least 10 characters', trigger: 'blur' }
        ]
      }
    }
  },

  mounted() {
    this.fetchTickets()
  },

  methods: {
    openNewTicketDialog() {
      this.dialogVisible = true
      this.newTicket = {
        subject: '',
        content: ''
      }
    },

    handleTicketClick(ticket) {
      if (this.selectedTicketId === ticket._id.$id) {
        this.selectedTicketId = null
        this.selectedTicket = null
      } else {
        this.selectedTicketId = ticket._id.$id
        this.selectedTicket = ticket
      }
    },

    getStatusType(status) {
      switch (status) {
        case 'open':
          return 'warning'
        case 'replied':
        case 'closed':
          return 'success'
        default:
          return 'info'
      }
    },

    async fetchTickets() {
      try {
        this.isLoading = true
        const response = await postRequest('getSupportTicketList', {
          user_id: getUserId(),
          user_type: getUserType()
        })
        if (response.data.code === 0) {
          this.tickets = response.data.data || []
          // Sort by create_time descending (newest first)
          this.tickets.sort((a, b) => (b.create_time || 0) - (a.create_time || 0))
        } else {
          this.$message.error(response.data.message || 'Failed to load tickets')
        }
      } catch (err) {
        console.error('Error fetching tickets:', err)
        this.$message.error('Failed to load support tickets')
      } finally {
        this.isLoading = false
      }
    },

    async submitTicket() {
      try {
        await this.$refs.ticketForm.validate()
        this.submitting = true

        const response = await postRequest('sendSupportTicket', {
          user_id: getUserId(),
          user_type: getUserType(),
          subject: this.newTicket.subject,
          content: this.newTicket.content
        })

        if (response.data.code === 0) {
          this.$message.success('Support ticket submitted successfully')
          this.dialogVisible = false
          this.fetchTickets()
        } else {
          this.$message.error(response.data.message || 'Failed to submit ticket')
        }
      } catch (error) {
        if (error !== false) { // Form validation returns false on error
          console.error('Error submitting ticket:', error)
          this.$message.error('Failed to submit ticket')
        }
      } finally {
        this.submitting = false
      }
    },

    formatDate(unixtime) {
      if (!unixtime) return ''
      const date = new Date(unixtime * 1000)
      return date.toLocaleString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      })
    },

    handleDialogClose() {
      this.$refs.ticketForm?.resetFields()
      this.dialogVisible = false
    }
  }
}
</script>

<!-- ------------------------------------------ style ------------------------------------------ -->
<style scoped lang="scss">
.support-tickets-page {
  padding: 20px;
}

.instruction-box {
  background-color: #f5f7fa;
  border-radius: 8px;
  padding: 16px 20px;
  margin-bottom: 24px;

  h3 {
    color: #303133;
    margin: 0 0 8px 0;
    font-size: 16px;
  }

  p {
    color: #606266;
    margin: 0;
    font-size: 14px;
    line-height: 1.6;
  }
}

.tickets-container {
  display: flex;
  gap: 20px;
  min-height: 500px;
}

.tickets-list {
  width: 100%;
  flex-shrink: 0;

  &.with-detail {
    max-width: 500px;
  }
}

.ticket-item {
  background: white;
  border-radius: 8px;
  padding: 16px;
  margin-bottom: 12px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  cursor: pointer;
  transition: all 0.2s;

  &:hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
  }

  &.selected {
    border: 2px solid #409EFF;
  }

  .ticket-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 8px;

    h3 {
      margin: 0;
      font-size: 16px;
      color: #303133;
      flex: 1;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
      margin-right: 10px;
    }
  }

  .ticket-content-preview {
    color: #606266;
    font-size: 14px;
    margin: 0 0 10px 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .ticket-meta {
    font-size: 12px;
    color: #909399;
    display: flex;
    gap: 15px;
  }
}

.ticket-detail {
  flex: 1;
  background: white;
  border-radius: 8px;
  padding: 24px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: relative;
  max-height: calc(100vh - 200px);
  overflow-y: auto;

  .close-button {
    position: absolute;
    top: 16px;
    right: 16px;
    font-size: 20px;
  }

  .detail-content {
    .detail-header {
      display: flex;
      align-items: center;
      gap: 12px;
      margin-bottom: 10px;

      h2 {
        margin: 0;
        font-size: 20px;
        color: #303133;
      }
    }

    .ticket-meta {
      font-size: 13px;
      color: #909399;
      margin-bottom: 20px;
    }

    .ticket-message {
      background: #f5f7fa;
      border-radius: 8px;
      padding: 16px;

      h4 {
        margin: 0 0 10px 0;
        font-size: 14px;
        color: #606266;
      }

      p {
        margin: 0;
        color: #303133;
        line-height: 1.6;
        white-space: pre-wrap;
      }
    }
  }
}

.reply-section {
  margin-top: 24px;
  padding-top: 24px;
  border-top: 1px solid #eee;

  h3 {
    margin: 0 0 16px 0;
    font-size: 16px;
    color: #303133;
  }

  .reply-item {
    background: #e8f4ff;
    border-radius: 8px;
    padding: 16px;
    border-left: 4px solid #409EFF;

    .reply-meta {
      display: flex;
      justify-content: space-between;
      margin-bottom: 10px;

      .reply-author {
        font-weight: 600;
        color: #409EFF;
      }

      .reply-time {
        font-size: 12px;
        color: #909399;
      }
    }

    .reply-content {
      color: #303133;
      line-height: 1.6;
      white-space: pre-wrap;
    }
  }
}

.waiting-reply {
  margin-top: 24px;
  padding: 20px;
  background: #fdf6ec;
  border-radius: 8px;
  text-align: center;
  color: #e6a23c;

  i {
    margin-right: 8px;
    font-size: 18px;
  }
}

.dialog-footer {
  text-align: right;
  display: block;
}
</style>
